import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { useT, useLang } from "@/contexts/LanguageContext";

/**
 * Reusable "locked feature" card shown when a free/lower-tier user lands on a
 * gated screen. Communicates VALUE first, then offers the upgrade CTA.
 *
 * Props:
 *  - title:        short headline (already localized by caller)
 *  - description:  1-2 sentence explanation (already localized by caller)
 *  - requiredPlan: "Starter" | "Growth" | "Pro" — purely informational
 *  - testid:       data-testid prefix
 *  - children:     optional extra content rendered above the CTA
 *  - inline:       when true, renders as a compact horizontal bar
 */
export default function LockedCard({
  title,
  description,
  requiredPlan = "Starter",
  testid = "locked-card",
  children,
  inline = false,
}) {
  const t = useT();
  const { lang } = useLang();
  const eyebrow = lang === "en"
    ? `Unlocks on the ${requiredPlan} plan`
    : `${requiredPlan} planında açılır`;
  const ctaLabel = t("locked.cta");

  if (inline) {
    return (
      <div
        className="flex items-center gap-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3"
        data-testid={testid}
      >
        <Lock size={14} className="text-cyan-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{title}</div>
          {description && <div className="text-xs text-slate-400 truncate">{description}</div>}
        </div>
        <Link
          to="/app/planlar"
          className="neon-btn rounded-lg px-3 py-1.5 text-xs text-white inline-flex items-center gap-1 shrink-0"
          data-testid={`${testid}-cta`}
        >
          {ctaLabel} <ArrowUpRight size={11} />
        </Link>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-3xl glass-strong border border-cyan-500/20 p-8"
      data-testid={testid}
    >
      <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative">
        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4">
          <Lock size={20} className="text-cyan-300" strokeWidth={1.6} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 mb-2">
          {eyebrow}
        </div>
        <h3 className="font-display text-2xl font-semibold text-white mb-2">{title}</h3>
        <p className="text-sm text-slate-300 leading-relaxed max-w-2xl mb-5">{description}</p>
        {children}
        <Link
          to="/app/planlar"
          className="neon-btn rounded-xl px-5 py-2.5 text-sm text-white inline-flex items-center gap-2 font-medium"
          data-testid={`${testid}-cta`}
        >
          {ctaLabel} <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
